import { HeaderPage, InputForm, LinkButton, Paginator, PopUpGeneric, TableGeneric } from '@components/index'
import { defaultPagination } from '@components/paginator/paginator-utils'
import { IApartments } from '@interfaces/apartments/IApartments'
import { ISearchFilters } from '@interfaces/apartments/IApartmentsFilters'
import { IColumn, IListPaginated } from '@interfaces/index'
import { errorHandler } from '@services/index'
import ApartmentService from '@services/requests/apartmentService'
import { IParamsUrl } from '@utils/getQueryParams/getQueryParams'
import { getQueryParams, totalPages } from '@utils/index'
import React from 'react'
import { confirmAlert } from 'react-confirm-alert'
import { Controller, useForm } from 'react-hook-form'
import { AiOutlineSearch } from 'react-icons/ai'
import { createSearchParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

export default function Apartments(): React.ReactElement {

    const paramsUrl = getQueryParams()
    const navigate = useNavigate()
    const [data, setData] = React.useState<IListPaginated<IApartments>>(defaultPagination)
    const [isLoading, setIsLoading] = React.useState(false)

    const { handleSubmit, control, reset } = useForm<ISearchFilters>()

    const columns: IColumn<IApartments>[] = React.useMemo<IColumn<IApartments>[]>(
        () => [
            {
                header: 'Imóvel',
                dataIndex: 'id',
                size: '30%',
                render: (item) => item.realEstate.name,
            },
            {
                header: 'Número do Apto.',
                dataIndex: 'number',
                size: '15%'
            },
            {
                header: 'Proprietário',
                dataIndex: 'id',
                render: (item) => item.user.name,
            },
            {
                header: 'Ação',
                dataIndex: 'actions',
                align: 'align-center',
                render: (item) => (
                    <div className="col-actions">
                        <LinkButton title='Editar' path={`/apartamentos/editar/${item.id}`} />
                        <a role="button" className='link-button' onClick={() => popUpConfirm(item.id)}>
                            Excluir
                        </a>
                    </div>
                ),
            },
        ],
        [data]
    )

    React.useEffect(() => {
        if (paramsUrl) {
            getList(paramsUrl)
            reset(paramsUrl)
        }
    }, [paramsUrl])

    async function getList(paramsUrl?: IParamsUrl): Promise<void> {
        setIsLoading(true)
        try {
            const response = await ApartmentService.list(paramsUrl)
            setData(response)
        } catch (err) {
            toast(errorHandler(err).message, { type: 'error' })
        } finally {
            setIsLoading(false)
        }
    }

    function submit(values: ISearchFilters): void {
        navigate(
            {
                pathname: '/apartamentos',
                search: createSearchParams({
                    ...values,
                    page: '1',
                }).toString()
            }
        )
    }

    function popUpConfirm(id: number): void {
        confirmAlert({
            customUI: ({ onClose }) => (
                <PopUpGeneric
                    onConfirm={() => {
                        deleteItem(id)
                        onClose()
                    }}
                    onCancel={onClose}
                    title={'Deseja excluir esse apartamento?'}
                />
            ),
        })
    }

    async function deleteItem(id: number): Promise<void> {
        try {
            const response = await ApartmentService.delete(id)
            toast(response.message, { type: 'success' })
            getList(paramsUrl)
        } catch (err) {
            toast(errorHandler(err).message, { type: 'error' })
        }
    }

    return (
        <div className="card">
            <div className="apartments">
                <HeaderPage title="Apartamentos" textButton="Novo Apartamento" onClickButton={() => navigate('/apartamentos/cadastrar')} />
                <form className='apartment-filters' onSubmit={handleSubmit(submit)}>
                    <Controller
                        control={control}
                        name='search'
                        render={({ field: { value, onChange } }) => (
                            <InputForm
                                size="small"
                                value={value}
                                onChange={(e) => { onChange(e) }}
                                inputProps={{ maxLength: 50 }}
                                onInputCallBack={() => {
                                    handleSubmit(submit)()
                                }}
                                placeholder="Buscar por proprietário"
                                style={{ width: '23rem', marginRight: '1rem' }}
                                InputProps={{
                                    endAdornment: (
                                        <button type='submit' className='button-search'>
                                            <AiOutlineSearch />
                                        </button>
                                    )
                                }}
                            />
                        )}
                    />
                </form>
                <div>
                    <TableGeneric data={data.items} columns={columns} isLoading={isLoading} />
                    <div className="paginator">
                        <Paginator maxElements={7}
                            current={data.page}
                            total={totalPages(data.count, data.perPage)}
                            onClick={(page) => navigate(
                                {
                                    pathname: '/apartamentos',
                                    search: createSearchParams({
                                        ...paramsUrl,
                                        page: page.toString(),
                                    }).toString()
                                }
                            )}
                        />
                    </div>
                </div>
            </div >
        </div >
    )
}
